import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Ban, Loader2, UserCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { BackLink } from "@/components/conecta/Bits";
import { MemberAvatar } from "@/components/red/MemberAvatar";
import { FounderBadge } from "@/components/red/FounderBadge";
import { Section } from "@/components/common/DetailFields";

const RelationActions = ({ p, onAction }) => {
  const r = p.relacion;
  if (p.es_yo) return <p data-testid="profile-is-me" className="text-sm c-text-2 text-center">Así te ve tu red.</p>;
  return (
    <div className="space-y-2">
      {r.estado === "ninguna" && <button data-testid="profile-connect-btn" onClick={() => onAction("solicitar")} className="c-btn-primary w-full py-3 flex items-center justify-center gap-2"><UserPlus className="w-4 h-4" /> Solicitar conexión</button>}
      {r.estado === "pendiente_enviada" && <p data-testid="profile-request-sent" className="c-card p-3 text-sm text-center c-text-2">Solicitud enviada</p>}
      {r.estado === "pendiente_recibida" && (
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="profile-accept-btn" onClick={() => onAction("aceptar")} className="c-btn-primary py-3">Aceptar</button>
          <button data-testid="profile-reject-btn" onClick={() => onAction("rechazar")} className="py-3 rounded-xl border c-line font-semibold">Rechazar</button>
        </div>
      )}
      {r.estado === "activa" && <p data-testid="profile-connected" className="c-card p-3 text-sm text-center flex items-center justify-center gap-1.5"><UserCheck className="w-4 h-4 c-accent" /> Conectados · 1er círculo</p>}
      {r.estado === "bloqueada" && <p data-testid="profile-blocked" className="c-card p-3 text-sm text-center c-text-2">Bloqueaste a este usuario</p>}
      {r.estado !== "bloqueada" && (
        <button data-testid="profile-block-btn" onClick={() => window.confirm(`¿Bloquear a ${p.nombre_publico}? No aparecerá en tu red ni tú en la suya.`) && onAction("bloquear")}
          className="w-full py-2.5 text-sm c-muted flex items-center justify-center gap-1.5"><Ban className="w-4 h-4" /> Bloquear</button>
      )}
    </div>
  );
};

export default function MemberProfilePage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => api.get(`/red/miembros/${id}`).then((r) => setP(r.data)).catch(() => setMissing(true)), [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (a) => {
    try {
      if (a === "solicitar") await api.post("/red/conexiones", { user_id: id });
      if (a === "bloquear") await api.post("/red/conexiones/bloquear", { user_id: id });
      if (a === "aceptar" || a === "rechazar") await api.post(`/red/conexiones/${p.relacion.connection_id}/${a}`);
      toast.success({ solicitar: "Solicitud enviada", bloquear: "Usuario bloqueado", aceptar: "Conexión aceptada", rechazar: "Solicitud rechazada" }[a]);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (missing) return <div data-testid="member-profile-missing" className="space-y-3"><BackLink to="/conecta/mi-red" label="Mi red" /><p className="c-card p-6 text-sm text-center c-text-2">Perfil no disponible.</p></div>;
  if (!p) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  return (
    <div data-testid="member-profile-page" className="space-y-3">
      <BackLink to="/conecta/mi-red" label="Mi red" />
      <div className="c-card p-5 flex flex-col items-center text-center gap-2">
        <MemberAvatar m={p} size="w-20 h-20 text-2xl" />
        <h2 data-testid="profile-name" className="font-heading text-2xl font-extrabold">{p.nombre_publico}</h2>
        <FounderBadge estado={p} testid="profile-member-founder" />
        <p className="text-sm c-text-2">{[p.ciudad, p.relacion.circulo && `${p.relacion.circulo}er círculo`.replace("2er", "2do")].filter(Boolean).join(" · ")}</p>
        {p.avalado_por && <p data-testid="profile-avalado" className="text-xs c-text-2">Avalado por <Link to={`/conecta/u/${p.avalado_por.id}`} className="font-semibold c-accent">{p.avalado_por.nombre_publico}</Link></p>}
        <p data-testid="profile-response-level" className="text-xs px-2.5 py-1 rounded-full bg-[var(--c-elev)]">Nivel de respuesta: {p.nivel_respuesta.etiqueta}</p>
      </div>
      <RelationActions p={p} onAction={act} />
      <Section title={`Contactos compartidos (${p.total_compartibles})`} testid="profile-areas">
        {p.compartibles_por_area.length === 0 ? <p className="text-sm c-muted py-2">Aún no comparte contactos.</p> : (
          <ul className="divide-y c-line">
            {p.compartibles_por_area.map((a) => <li key={a.area} data-testid="profile-area-item" className="py-2 flex justify-between text-sm"><span>{a.nombre}</span><span className="font-semibold tabular-nums">{a.cantidad}</span></li>)}
          </ul>
        )}
        <p className="text-[11px] c-muted pt-2">Solo conteos por área. Nadie ve los datos de los contactos de otro miembro.</p>
      </Section>
    </div>
  );
}
